import { useMemo, useState } from 'react'
import { buildAgenda, type AgendaDay } from '@core/calendar'
import { completeTask, type LetterTask } from '@core/letters'
import { byTime, describeWhen, type Appointment } from '@core/appointments'
import { describeRepeat } from '@core/taskRepeat'
import { formatCents } from '@core/money'
import { remainingCents, type Payable } from '@core/payments'
import { Button, Card } from '@design/components'
import { Tags } from '@design/Tags'
import { PaymentCard } from './PaymentCard'

/**
 * What is next, as a list.
 *
 * The grid answers "which days are heavy" and cannot answer "what is next" —
 * on the 28th it shows three days and then a wall, which is the week you most
 * need to see past. This is the same data read the other way: only days that
 * have something, in order, straight through the end of the month.
 *
 * Late things come first and are separated by a line, not by a colour alone.
 * Something that was due last Tuesday is not in the past; it is the most
 * present thing you have.
 */
export function AgendaView({
  payables,
  tasks,
  appointments,
  userId,
  onChanged,
  onNewAppointment,
  onOpenAppointment,
}: {
  payables: Payable[]
  tasks: LetterTask[]
  appointments: Appointment[]
  userId: string
  onChanged: () => void
  onNewAppointment: () => void
  onOpenAppointment: (a: Appointment) => void
}) {
  const [open, setOpen] = useState<Payable | null>(null)
  const days = useMemo(
    () => buildAgenda(payables, tasks, appointments),
    [payables, tasks, appointments],
  )

  if (days.length === 0) {
    return (
      <>
        <Card className="text-center py-8 mb-3">
          <p className="font-medium mb-1">Niets op de agenda</p>
          <p className="text-muted text-sm">De komende weken staat er niets klaar.</p>
        </Card>
        <Button tone="secondary" onClick={onNewAppointment}>
          Afspraak inplannen
        </Button>
      </>
    )
  }

  const late = days.filter((d) => d.overdue)
  const ahead = days.filter((d) => !d.overdue)

  return (
    <>
      {late.length > 0 && (
        <section className="mb-5">
          <h2 className="text-sm text-danger mb-2">Te laat · {late.length}</h2>
          {late.map((day) => (
            <DayBlock
              key={day.date}
              day={day}
              userId={userId}
              onOpen={setOpen}
              onChanged={onChanged}
              onOpenAppointment={onOpenAppointment}
            />
          ))}
        </section>
      )}

      {ahead.map((day) => (
        <DayBlock
          key={day.date}
          day={day}
          userId={userId}
          onOpen={setOpen}
          onChanged={onChanged}
          onOpenAppointment={onOpenAppointment}
        />
      ))}

      <Button tone="secondary" onClick={onNewAppointment}>
        Afspraak inplannen
      </Button>

      {open && (
        <PaymentCard
          payable={open}
          userId={userId}
          onClose={() => setOpen(null)}
          onPaid={() => {
            setOpen(null)
            onChanged()
          }}
        />
      )}
    </>
  )
}

function DayBlock({
  day,
  userId,
  onOpen,
  onChanged,
  onOpenAppointment,
}: {
  day: AgendaDay
  userId: string
  onOpen: (p: Payable) => void
  onChanged: () => void
  onOpenAppointment: (a: Appointment) => void
}) {
  const [busy, setBusy] = useState<string | null>(null)

  return (
    <section className="mb-4">
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <h3 className={`text-sm font-medium ${day.isToday ? '' : 'text-muted'}`}>
          {day.isToday ? 'Vandaag' : dayLabel(day.date)}
        </h3>
        {day.dueCents > 0 && (
          <span className="text-xs text-muted tabular-nums">{formatCents(day.dueCents)}</span>
        )}
      </div>

      {/* Appointments lead the day. They are the only thing here with an hour,
          and an hour is what orders a day. */}
      {[...day.appointments].sort(byTime).map((a) => (
        <button key={a.id} onClick={() => onOpenAppointment(a)} className="w-full text-left">
          <Card className="mb-2 flex items-baseline gap-3">
            <span className="shrink-0 tabular-nums text-sm font-medium">{describeWhen(a)}</span>
            <div className="min-w-0">
              <p className="font-medium truncate">{a.title}</p>
              {a.location && <p className="text-sm text-muted truncate">{a.location}</p>}
            </div>
          </Card>
        </button>
      ))}

      {[...day.due, ...day.planned].map((item) => (
        <button key={item.instalmentId} onClick={() => onOpen(item)} className="w-full text-left">
          <Card className="mb-2 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{item.supplierName ?? item.description}</p>
              <Tags
                spaceName={item.spaceName}
                spaceKind={item.spaceKind}
                category={item.category}
              />
            </div>
            <span className="shrink-0 tabular-nums font-medium">
              {item.isEstimate && '± '}
              {formatCents(remainingCents(item))}
            </span>
          </Card>
        </button>
      ))}

      {day.tasks.map((task) => (
        <Card key={task.id} className="mb-2 flex items-start gap-3">
          <button
            aria-label="Afvinken"
            disabled={busy === task.id}
            onClick={async () => {
              setBusy(task.id)
              try {
                await completeTask(task, userId)
                onChanged()
              } finally {
                setBusy(null)
              }
            }}
            className="shrink-0 mt-0.5 w-7 h-7 rounded-full border-2 border-line active:border-ink"
          />
          <div className="min-w-0">
            <p className="font-medium truncate">{task.title}</p>
            {task.assignmentNote && (
              <p className="text-sm text-muted truncate">{task.assignmentNote}</p>
            )}
            <p className="text-xs text-muted">
              {[task.kind === 'letter' ? 'Brief' : 'Te doen', describeRepeat(task.repeatRule)]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
        </Card>
      ))}
    </section>
  )
}

/** "vrijdag 13 maart" — the weekday is what a person navigates by. */
function dayLabel(iso: string): string {
  return new Intl.DateTimeFormat('nl-BE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(`${iso}T00:00:00`))
}
